'use client';

import { type MouseEvent, useEffect, useRef, useState } from 'react';

import { ChevronUp } from 'lucide-react';

type Section = { id: string; number: number; title: string };

function linkClass(isActive: boolean) {
  return `grid min-h-11 grid-cols-[1.5rem_minmax(0,1fr)] items-center gap-2 border-s-2 py-2 pe-2 ps-3 text-sm leading-snug transition-colors duration-150 hover:bg-slate-50 hover:text-slate-950 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-950 ${
    isActive ? 'border-slate-900 font-semibold text-slate-950' : 'border-transparent text-slate-600'
  }`;
}

function SectionList({
  activeId,
  onSelect,
  sections,
}: {
  activeId: string | undefined;
  onSelect: (id: string) => void;
  sections: Section[];
}) {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = document.getElementById(id);
    if (!target) return;

    event.preventDefault();
    const hash = `#${id}`;
    if (window.location.hash !== hash) window.history.pushState(window.history.state, '', hash);
    target.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      block: 'start',
    });
    onSelect(id);
  };

  return (
    <ol className="divide-y divide-slate-100">
      {sections.map(({ id, number, title }) => (
        <li key={id}>
          <a
            aria-current={id === activeId ? 'location' : undefined}
            className={linkClass(id === activeId)}
            href={`#${id}`}
            onClick={(event) => handleClick(event, id)}
          >
            <span
              aria-hidden="true"
              className={
                id === activeId ? 'text-xs tabular-nums text-slate-900' : 'text-xs tabular-nums text-slate-400'
              }
            >
              {String(number).padStart(2, '0')}
            </span>
            <span>{title}</span>
          </a>
        </li>
      ))}
    </ol>
  );
}

export function TourNavigation({ label, sections }: { label: string; sections: Section[] }) {
  const details = useRef<HTMLDetailsElement>(null);
  const [showMobileNavigation, setShowMobileNavigation] = useState(false);
  const [activeId, setActiveId] = useState(sections[0]?.id);

  useEffect(() => {
    if (!showMobileNavigation) details.current?.removeAttribute('open');
  }, [showMobileNavigation]);

  useEffect(() => {
    const closeOnOutsidePointerDown = (event: PointerEvent) => {
      if (details.current?.open && event.target instanceof Node && !details.current.contains(event.target)) {
        details.current.removeAttribute('open');
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && details.current?.open) {
        details.current.removeAttribute('open');
        details.current.querySelector('summary')?.focus();
      }
    };

    document.addEventListener('pointerdown', closeOnOutsidePointerDown);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointerDown);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  useEffect(() => {
    const elements = sections
      .map(({ id }) => ({ element: document.getElementById(id), id }))
      .filter((section): section is { id: string; element: HTMLElement } => section.element !== null);
    if (elements.length === 0) return;

    let frame = 0;
    const updateActive = () => {
      frame = 0;
      const readingLine = window.innerHeight * 0.3;
      let current = elements[0].id;
      for (const { id, element } of elements) {
        if (element.getBoundingClientRect().top > readingLine) break;
        current = id;
      }
      setActiveId(current);

      const firstTop = elements[0].element.getBoundingClientRect().top;
      const lastTop = elements[elements.length - 1].element.getBoundingClientRect().top;
      const entryLine = window.innerHeight * 0.75;
      setShowMobileNavigation(firstTop <= entryLine && lastTop > entryLine);
    };
    const scheduleUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(updateActive);
    };

    updateActive();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [sections]);

  const activeSection = sections.find(({ id }) => id === activeId) ?? sections[0];
  const activeTitle = activeSection?.title;
  const activeIndex = String(activeSection?.number ?? 1).padStart(2, '0');
  const sectionCount = String(sections.length).padStart(2, '0');

  return (
    <>
      <div
        aria-hidden={!showMobileNavigation}
        className={`fixed inset-x-4 z-40 mx-auto max-w-md transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none lg:hidden ${
          showMobileNavigation ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-full opacity-0'
        }`}
        data-tour-mobile-navigation
        inert={!showMobileNavigation}
        style={{ bottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <details className="group relative rounded-2xl border border-slate-200 bg-white shadow-lg" ref={details}>
          <summary className="flex min-h-14 cursor-pointer items-center gap-3 px-4 py-2 text-slate-900">
            <span aria-hidden="true" className="shrink-0 text-xs font-semibold tabular-nums text-slate-500">
              {activeIndex} / {sectionCount}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
              <span className="block truncate text-sm font-semibold">{activeTitle}</span>
            </span>
            <ChevronUp aria-hidden="true" className="size-4 shrink-0 group-open:rotate-180" />
          </summary>
          <nav
            aria-label={label}
            className="absolute inset-x-0 bottom-[calc(100%+0.5rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg"
          >
            <div className="max-h-[min(52svh,22rem)] overscroll-contain overflow-y-auto">
              <SectionList
                activeId={activeId}
                onSelect={(id) => {
                  setActiveId(id);
                  details.current?.removeAttribute('open');
                }}
                sections={sections}
              />
            </div>
          </nav>
        </details>
      </div>

      <aside className="hidden lg:block">
        <nav
          aria-label={label}
          className="sticky top-8 max-h-[calc(100vh-4rem)] overflow-y-auto border-s border-slate-200"
        >
          <p className="mb-4 px-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
          <SectionList activeId={activeId} onSelect={setActiveId} sections={sections} />
        </nav>
      </aside>
    </>
  );
}
